import { produce } from 'immer';

import {
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_COMPLETE,
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_FAILURE,
  CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_SUCCESS,
  CONTRACT_AUTHENTICATION_EXCEED_COUNT_INITIALIZE,
  CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_SUCCESS,
  CONTRACT_AUTHENTICATION_INITIALIZE,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_FAILURE,
  CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_COMPLETE_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_COMPLETE_SUCCESS,
  CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_INITIALIZE,
  CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_UPDATE,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_FAILURE,
  CONTRACT_AUTHENTICATION_PHONE_RETRY_SUCCESS,
  CONTRACT_SETUP_ALL_STATE_CLEAR,
} from '@app/store/actionTypes';
import {
  ContractAuthenticationAction,
  fetchContractAuthentication,
} from '@app/store/contract-authentication/actions';
import ContractAuthentication from '@app/store/contract-authentication/ContractAuthentication';
import ContractAuthenticationStatus from '@app/store/contract-authentication/ContractAuthenticationStatus';
import { ContractSetupAction } from '@app/store/contract-setup/actions';

import ContractAuthenticationDetailStatus from '../ContractAuthenticationDetailStatus';

export type ContractAuthenticationAuthenticationsState = ContractAuthentication;

const initialState: ContractAuthentication = {
  digitalCertificate: null,
  mobile: null,
  password: null,
};

export default function reducer(
  state: ContractAuthenticationAuthenticationsState = initialState,
  action: ContractAuthenticationAction | ContractSetupAction
): ContractAuthenticationAuthenticationsState {
  if (fetchContractAuthentication.pending.match(action)) {
    return initialState;
  }

  if (fetchContractAuthentication.fulfilled.match(action)) {
    return action.payload.authentication;
  }

  switch (action.type) {
    case CONTRACT_AUTHENTICATION_INITIALIZE:
    case CONTRACT_SETUP_ALL_STATE_CLEAR:
      return initialState;
    case CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_COMPLETE:
      return produce(state, (draft) => {
        if (draft.digitalCertificate) {
          draft.digitalCertificate.status = ContractAuthenticationStatus.SUCCEEDED;
        }
      });
    case CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_SUCCESS:
      return produce(state, (draft) => {
        const { answer } = action.payload;
        if (draft.digitalCertificate) {
          draft.digitalCertificate.bizNumberLastDigits = answer;
        }
      });
    case CONTRACT_AUTHENTICATION_DIGITAL_CERTIFICATE_GUIDE_CHECK_FAILURE:
      return produce(state, (draft) => {
        if (draft.digitalCertificate) {
          const { failureInfo } = action.payload;
          if (failureInfo) {
            draft.digitalCertificate.failureCount = failureInfo.failureCount;
            draft.digitalCertificate.lastFailedAt = failureInfo.lastFailedAt;
            draft.digitalCertificate.retryAt = failureInfo.retryAt;
            draft.digitalCertificate.status = failureInfo.retryAt
              ? ContractAuthenticationStatus.EXCEEDED
              : ContractAuthenticationStatus.FAILED;
          } else {
            draft.digitalCertificate.status = ContractAuthenticationStatus.FAILED;
          }
        }
      });
    case CONTRACT_AUTHENTICATION_PHONE_COMPLETE_SUCCESS:
      return produce(state, (draft) => {
        const { failureCount, lastFailedAt, retryAt, status } = action.payload;
        if (draft.mobile) {
          draft.mobile.lastFailedAt = lastFailedAt;
          draft.mobile.failureCount = failureCount;
          draft.mobile.retryAt = retryAt;
          draft.mobile.status = status;
        }
      });
    case CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_SUCCESS:
      return produce(state, (draft) => {
        const { failureCount, lastFailedAt, retryAt, status } = action.payload;
        if (draft.password) {
          draft.password.failureCount = failureCount;
          draft.password.lastFailedAt = lastFailedAt;
          draft.password.status = status;
          draft.password.retryAt = retryAt;
        }
      });
    case CONTRACT_AUTHENTICATION_PASSWORD_AUTHENTICATE_FAILURE:
      return produce(state, (draft) => {
        const { error, failureInfo } = action.payload;
        if (draft.password && failureInfo) {
          // 에러로 실패 액션을 받은 경우 failureCount, lastFailedAt 갱신할 수 없습니다.
          if (!error) {
            draft.password.failureCount = failureInfo.failureCount;
            draft.password.lastFailedAt = failureInfo.lastFailedAt;
          }
          draft.password.status = failureInfo.status;
          draft.password.retryAt = failureInfo.retryAt;
        }
      });
    case CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_SUCCESS:
    case CONTRACT_AUTHENTICATION_PHONE_RETRY_SUCCESS:
      return produce(state, (draft) => {
        if (draft.mobile) {
          const { answer } = action.payload;
          draft.mobile.failureCount = 0;
          draft.mobile.lastFailedAt = null;
          draft.mobile.phoneNumberLastDigits = answer;
        }
      });
    case CONTRACT_AUTHENTICATION_PHONE_COMPLETE_FAILURE:
    case CONTRACT_AUTHENTICATION_PHONE_CERTIFICATE_FAILURE:
    case CONTRACT_AUTHENTICATION_PHONE_RETRY_FAILURE:
      return produce(state, (draft) => {
        if (draft.mobile) {
          const { failureInfo } = action.payload;
          if (failureInfo) {
            draft.mobile.failureCount = failureInfo.failureCount;
            draft.mobile.lastFailedAt = failureInfo.lastFailedAt;
            draft.mobile.leftFailureCount = failureInfo.leftFailureCount;
            draft.mobile.retryAt = failureInfo.retryAt;
            draft.mobile.status = failureInfo.retryAt
              ? ContractAuthenticationStatus.EXCEEDED
              : ContractAuthenticationStatus.FAILED;
          } else {
            const { error } = action.payload;
            if (error) {
              draft.mobile.errorMessage = error.message;
              draft.mobile.status = ContractAuthenticationStatus.FAILED;
            }
          }
          draft.mobile.authenticationDetailStatus = action.payload.authenticationDetailStatus;
        }
      });
    case CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_INITIALIZE:
      return produce(state, (draft) => {
        if (draft.mobile) {
          draft.mobile.authenticationDetailStatus = ContractAuthenticationDetailStatus.READY;
        }
      });
    case CONTRACT_AUTHENTICATION_PHONE_DETAIL_STATUS_UPDATE:
      return produce(state, (draft) => {
        if (draft.mobile) {
          const { authenticationDetailStatus } = action.payload;
          draft.mobile.authenticationDetailStatus = authenticationDetailStatus;
        }
      });
    case CONTRACT_AUTHENTICATION_EXCEED_COUNT_INITIALIZE:
      return produce(state, (draft) => {
        if (draft.digitalCertificate) {
          draft.digitalCertificate.retryAt = null;
          draft.digitalCertificate.status = ContractAuthenticationStatus.FAILED;
        }
        if (draft.mobile) {
          draft.mobile.retryAt = null;
          draft.mobile.status = ContractAuthenticationStatus.FAILED;
        }
        if (draft.password) {
          draft.password.retryAt = null;
          draft.password.status = ContractAuthenticationStatus.FAILED;
        }
      });
    case CONTRACT_AUTHENTICATION_FAILURE_STATUS_FETCH_SUCCESS:
      return produce(state, (draft) => {
        // 법인 공동 인증서의 경우에만 액션을 사용하고 있어서 법인 공동인증서만 수정합니다.
        const { authType, failureCount, lastFailedAt, retryAt } = action.payload;
        if (draft.digitalCertificate && authType === 'D_CERT') {
          draft.digitalCertificate.failureCount = failureCount;
          draft.digitalCertificate.lastFailedAt = lastFailedAt;
          draft.digitalCertificate.retryAt = retryAt;

          draft.digitalCertificate.status = retryAt
            ? ContractAuthenticationStatus.EXCEEDED
            : ContractAuthenticationStatus.FAILED;
        }
      });
    default:
      return state;
  }
}
